/**
 * previewColorway -- which placement and colorway a single-artwork Artifi
 * preview request names, and what the server's answer means.
 *
 * P0 (Jake, 2026-10-28): "the preview never renders with the selected
 * colorway." #4721 moved the colorway decision to the SERVER: GET
 * /api/artifi/preview reads `placement_id` (single-artwork path), routes the
 * garment color to the variant that applies, and composites that variant's
 * recolored asset. The client never names a colorway; it names the placement
 * it is previewing. The editor stopped short of sending it, so every preview
 * came back in the design's default colors.
 *
 * The Stage (useArtifiPreviewCache) and the per-color render manager
 * (useStorefrontColorRenders) both build requests; both call
 * `previewColorwayArgs` so their requests, and therefore their cache keys,
 * cannot drift apart.
 */

/** The server's `colorway.state` vocabulary (colorway_preview_asset.py). */
export const COLORWAY_STATE = Object.freeze({
    // The bound colorway's recolored asset was composited.
    APPLIED: 'applied',
    // Another request is creating the asset: the image is the default colors.
    PENDING: 'pending',
    // It could not be created now: default colors, do not poll.
    UNAVAILABLE: 'unavailable',
})

/**
 * Delay before each re-request while the server says `pending`. A fixed,
 * small, increasing schedule: a Wilcom recolor plus an Artifi upload takes
 * seconds, and a request that is not the one minting never spends (the
 * server's claim row is the lock). After the last delay the default-colors
 * image stays on screen with its note; the next edit asks again.
 */
export const COLORWAY_PENDING_RETRY_MS = Object.freeze([3000, 6000, 12000])

// The server's placement_id pattern (artifi_preview._CAMPAIGN_PRODUCT_ID_RE):
// 1-12 digits, no leading zero. A staged placement (`tmp-N`) has no row yet.
const SERVER_ID = /^[1-9][0-9]{0,11}$/

/**
 * A saved placement's numeric id, or null when the value is not one the
 * server would accept (staged placement, missing id).
 *
 * @param {string|number|null|undefined} id
 * @returns {number|null}
 */
export function parsePlacementId(id) {
    if (id == null) return null
    const text = String(id)
    return SERVER_ID.test(text) ? Number(text) : null
}

/**
 * The placement identity for a preview request.
 *
 * `placementId` goes on the wire; `colorwayId` does NOT -- it only makes the
 * client's request identity (in-flight de-dup and cache key) change when the
 * placement's bound colorway changes, so a rebind can never be served the
 * previous colorway's image.
 *
 * Names nothing for a bare render (no artwork: nothing to recolor), for a
 * composite (the server reads every placement of the campaign product), or
 * for a placement that is not saved yet.
 *
 * @param {object} p
 * @param {object|null} p.placement  the editor placement (decoration) object
 * @param {object|null} p.variant    the variant that routes the garment color
 * @param {string|null} p.artworkFileId  the artwork the request composites
 * @param {boolean} [p.composite]
 * @returns {{placementId: number|null, colorwayId: number|null}}
 */
export function previewColorwayArgs({
    placement,
    variant,
    artworkFileId,
    composite = false,
}) {
    const none = { placementId: null, colorwayId: null }
    if (composite || !artworkFileId) return none
    const placementId = parsePlacementId(placement?.id)
    if (placementId == null) return none
    return { placementId, colorwayId: variant?.colorway_id ?? null }
}
